import type {
  ILauncherService,
  IModuleService,
  LaunchItem,
  ResolvedView,
  UserActivationApi,
  WorkbenchNavigationChangedPayload,
  WorkbenchNavigationSource,
} from '@hibro/shared';
import { externalProcessKey, launchItemLabelSource } from '@hibro/shared';
import { toast } from '@hibro/ui-client';

import { openAppLink, type OpenExternalBridge } from '../app-links/open';
import type { Translator } from '../i18n/i18n';
import { isDetachedInstance } from '../surfaces/detached-views';

/**
 * Display label for a {@link LaunchItem} (见 21). Three sources, three rules:
 * a `builtin` entry's title is a shell message key, an app-link or provider row
 * carries a literal string the user typed, and a module item's is a `%key%`
 * placeholder resolved against its owner's catalog.
 */
export function resolveLaunchItemLabel(
  item: LaunchItem,
  { t }: Pick<Translator, 't'>,
): string {
  const source = launchItemLabelSource(item);
  if (source.kind === 'literal') {
    return source.text;
  }
  // `t` degrades a missing message to the key itself; the label rule wants the
  // shorter fallback the source carries instead (见 launchItemLabelSource).
  const text = t(source.key);
  return text === source.key ? source.fallback : text;
}

export interface LaunchActivatorDeps {
  /** All contributed views, to resolve an `openView` action's target. */
  views: readonly ResolvedView[];
  detachedSurfaces?: ReadonlySet<string>;
  runningExternalProcesses?: ReadonlySet<string>;
  moduleService: IModuleService;
  launcherService: ILauncherService;
  /** Sync open for the browser runtime (见 21 / 18); keeps the user gesture. */
  openExternalBridge?: OpenExternalBridge;
  userActivation: UserActivationApi;
  onDispatch: (command: string) => void;
  onOpenView: (viewId: string) => void;
  onFocusDetached?: (viewId: string) => void;
  /** Focus a running external process's native window, by its process key. */
  onActivateExternal?: (processKey: string) => void;
  onReportNavigation?: (payload: WorkbenchNavigationChangedPayload) => void;
  translator: Pick<Translator, 't' | 'title'>;
}

/** Runs a launch item's declared action, attributing it to a navigation source. */
export type LaunchActivator = (
  item: LaunchItem,
  source: WorkbenchNavigationSource,
) => void;

/**
 * The one way to act on a {@link LaunchItem} (见 21 打开漏斗).
 *
 * Lives here rather than in `ActivitySlot` because the rail is no longer the
 * only face that opens a launchable: global search lists the same projection as
 * `/open <module>` rows, and the two must not drift on the parts that are easy
 * to forget — the detached-window check, the `resolveInstance` hand-off, the
 * external-process focus, and the three toast paths an `openUrl` can take.
 *
 * `source` is a parameter rather than baked in for the same reason: the item
 * decides *what* happens, the caller only says who asked.
 */
export function createLaunchActivator(
  deps: LaunchActivatorDeps,
): LaunchActivator {
  return (item, source) => {
    const action = item.action;
    if (!action) {
      return;
    }
    const label = resolveLaunchItemLabel(item, deps.translator);
    const { t } = deps.translator;

    deps.onReportNavigation?.({
      source,
      moduleId: item.moduleId,
      viewId: action.type === 'openView' ? action.viewId : null,
    });

    if (action.type === 'command') {
      deps.onDispatch(action.command);
      return;
    }

    if (action.type === 'openUrl') {
      const result = openAppLink(item, {
        bridge: deps.openExternalBridge,
        launcherService: deps.launcherService,
        openLink: (url) =>
          deps.moduleService.openLink?.(url, item.moduleId) ??
          Promise.resolve(false),
        openWindow: (url) => {
          void deps.userActivation
            .openBrowser({ url, presentation: 'window' })
            .catch(() =>
              toast(t('workbench.launch.blocked', { title: label })),
            );
        },
      });
      if (result.degraded) {
        toast(t('workbench.launch.unsupportedHosting', { title: label }));
      }
      if (!result.opened) {
        toast(t('workbench.launch.blocked', { title: label }));
      }
      return;
    }

    if (isDetachedInstance(deps.detachedSurfaces, action.viewId)) {
      deps.onFocusDetached?.(action.viewId);
      return;
    }
    if (action.resolveInstance) {
      deps.onDispatch(action.resolveInstance);
      return;
    }

    deps.onOpenView(action.viewId);

    const targetView = deps.views.find((view) => view.id === action.viewId);
    const processKey =
      targetView?.kind === 'external'
        ? externalProcessKey(targetView.moduleId, targetView.externalEntry)
        : undefined;
    if (
      processKey !== undefined &&
      deps.runningExternalProcesses?.has(processKey)
    ) {
      deps.onActivateExternal?.(processKey);
    }
  };
}
